import React, { useState, useEffect, useRef } from "react";
import { Loader2, Globe2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * OSMBulkImportCard
 *
 * Hits POST /api/admin/ingest/osm-bulk and then polls
 * GET /api/admin/ingest/osm-bulk/{job_id} every 3 seconds until the
 * job is `done` or `failed`. Surfaces inserted-count, total-after,
 * and dedup breakdown so the admin sees exactly what landed.
 *
 * One-tap recovery when a fresh deployment lands with a small DB:
 * brings every UK camp_site + caravan_site from OpenStreetMap (≈11k)
 * into the catalogue, idempotently.
 */
export default function OSMBulkImportCard() {
  const [phase, setPhase] = useState("idle"); // idle | running | done | failed
  const [job, setJob] = useState(null);
  const [stats, setStats] = useState(null);
  const pollRef = useRef(null);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/campsites/stats");
      setStats(data);
    } catch {}
  };
  useEffect(() => { loadStats(); }, []);

  const start = async () => {
    setPhase("running"); setJob(null);
    try {
      const { data } = await api.post("/admin/ingest/osm-bulk");
      const jobId = data.job_id;
      setJob({ status: "queued", job_id: jobId });
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/admin/ingest/osm-bulk/${jobId}`);
          setJob(r.data);
          if (r.data.status === "done") {
            clearInterval(pollRef.current);
            setPhase("done");
            toast.success(`Added ${r.data.inserted ?? 0} new UK campsites · ${r.data.total_after ?? "?"} total`);
            loadStats();
          } else if (r.data.status === "failed") {
            clearInterval(pollRef.current);
            setPhase("failed");
            toast.error(r.data.error || "Import failed");
          }
        } catch {}
      }, 3000);
    } catch (e) {
      setPhase("failed");
      toast.error(e?.response?.data?.detail || "Failed to start import");
    }
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const isRunning = phase === "running" && job && !["done", "failed"].includes(job.status);
  const statusLabel = (s) => ({
    queued: "Queued…",
    fetching_overpass: "Fetching OpenStreetMap (~30 s)…",
    deduping: "De-duplicating against existing DB…",
    inserting: "Inserting new sites…",
    done: "Done",
    failed: "Failed",
  }[s] || s);

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="osm-bulk-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="eyebrow mb-1">Production data sync</div>
          <div className="font-display text-xl mb-1">Bulk-import all UK campsites from OpenStreetMap</div>
          <p className="text-sm text-[#555]">
            Fetches every <code className="mono text-xs">tourism=camp_site</code> and
            {" "}<code className="mono text-xs">tourism=caravan_site</code> in the UK via the
            public Overpass API and inserts any that aren't already in this database. Idempotent —
            safe to re-run; dedup is by <code className="mono text-xs">osm_id</code>, 150&nbsp;m radius,
            and name + 5&nbsp;km Jaccard.
          </p>
          {stats && (
            <p className="text-xs text-[#888] mt-2 mono">
              Current total: <strong className="text-[#111]">{stats.total?.toLocaleString()}</strong> campsites
              · {stats.pitchup_listed?.toLocaleString()} Pitchup-listed
            </p>
          )}
        </div>
        <button
          onClick={start}
          disabled={isRunning}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="osm-bulk-start"
        >
          {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe2 className="w-4 h-4" />}
          {isRunning ? "Importing…" : "Sync from OpenStreetMap"}
        </button>
      </div>

      {job && (
        <div className="mt-4 p-4 bg-[#fafafa] border hairline text-sm" data-testid="osm-bulk-status">
          <div className="flex items-center gap-2 mb-2">
            {phase === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
             phase === "failed" ? <AlertTriangle className="w-4 h-4 text-[#ff4500]" /> :
             <Loader2 className="w-4 h-4 animate-spin text-[#ff4500]" />}
            <span className="font-medium">{statusLabel(job.status)}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mono text-xs">
            {job.overpass_count != null && <Stat k={job.overpass_count} v="OSM elements" />}
            {job.candidates != null && <Stat k={job.candidates} v="Candidates" />}
            {job.survivors != null && <Stat k={job.survivors} v="New (post-dedup)" />}
            {job.inserted != null && <Stat k={job.inserted} v="Inserted" highlight />}
            {job.total_after != null && <Stat k={job.total_after} v="Total now" highlight />}
            {job.dup_osm != null && <Stat k={job.dup_osm} v="Dup · osm_id" />}
            {job.dup_distance != null && <Stat k={job.dup_distance} v="Dup · 150 m" />}
            {job.dup_name != null && <Stat k={job.dup_name} v="Dup · name+5 km" />}
          </div>
          {job.error && (
            <pre className="mt-3 text-xs text-red-600 whitespace-pre-wrap">{job.error}</pre>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ k, v, highlight }) => (
  <div className={highlight ? "border-l-2 border-[#ff4500] pl-2" : ""}>
    <div className={`text-lg ${highlight ? "text-[#ff4500] font-bold" : "text-[#111]"}`}>{k?.toLocaleString()}</div>
    <div className="uppercase tracking-wider text-[10px] text-[#888]">{v}</div>
  </div>
);
